// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsProvider
 * @package @geenius/analytics/solidjs-css
 * @description Defines the provider boundary for the SolidJS CSS analytics
 * variant. The provider creates a tracker instance and exposes it to the
 * variant's primitives and components.
 */

import type {
  ConsentSettings,
  AnalyticsStorageAdapter,
  TrackerConfigInput,
  TrackerInstance,
} from "@geenius/analytics";
import { AnalyticsContextError, createTracker } from "@geenius/analytics";
import {
  createContext,
  createSignal,
  type ParentComponent,
  useContext,
} from "solid-js";

export type AnalyticsCopyTranslator = (
  key: string,
  params?: Record<string, string | number>,
) => string;

/**
 * Props accepted by {@link AnalyticsProvider}.
 *
 * @property config Tracker configuration used to create the provider instance.
 * @property translator Optional app-level copy translator used by analytics UI helpers.
 * @property children Descendant SolidJS CSS nodes that consume analytics context.
 * @property initialConsent Optional initial consent settings to apply on mount.
 * @property storage Optional self-hosted storage adapter used before outbound provider sync.
 */
export interface AnalyticsProviderProps {
  config: TrackerConfigInput;
  translator?: AnalyticsCopyTranslator;
  initialConsent?: ConsentSettings | null;
  storage?: AnalyticsStorageAdapter | AnalyticsStorageAdapter[];
}

export interface AnalyticsConsentContextValue {
  consentSettings: () => ConsentSettings | null;
  updateConsent: (settings: ConsentSettings) => void;
}

/**
 * Options for {@link useAnalyticsContext}.
 *
 * @property optional When true, returns `undefined` instead of throwing outside a provider.
 */
export interface UseAnalyticsContextOptions {
  optional?: boolean;
}

/**
 * Solid context carrying the active analytics tracker for the CSS variant.
 */
export const AnalyticsCtx = createContext<TrackerInstance>();

/**
 * Solid context carrying an optional host-app copy translator for analytics UI.
 */
export const AnalyticsCopyContext =
  createContext<AnalyticsCopyTranslator | null>(null);

/**
 * Solid context carrying the consent state and update callback.
 */
export const AnalyticsConsentContext =
  createContext<AnalyticsConsentContextValue | null>(null);

/**
 * Reads the current analytics tracker from Solid context.
 *
 * @param options Controls whether a missing provider should throw or return `undefined`.
 * @returns The active tracker instance, or `undefined` when `optional` is enabled.
 * @throws {AnalyticsContextError} When called outside a provider without `optional`.
 */
export function useAnalyticsContext(
  options: UseAnalyticsContextOptions = {},
): TrackerInstance | undefined {
  const context = useContext(AnalyticsCtx);

  if (!context && !options.optional) {
    throw new AnalyticsContextError({
      message:
        "useAnalyticsContext must be called inside an <AnalyticsProvider> tree.",
    });
  }

  return context;
}

/**
 * Reads the current copy translator from Solid context.
 *
 * @returns The active copy translator, or `null` when no translator is provided.
 */
export function useAnalyticsCopyTranslator(): AnalyticsCopyTranslator | null {
  return useContext(AnalyticsCopyContext) ?? null;
}

/**
 * Reads the current consent context from Solid context.
 *
 * @returns The consent context value, or `null` when outside a provider.
 */
export function useAnalyticsConsent(): AnalyticsConsentContextValue | null {
  return useContext(AnalyticsConsentContext) ?? null;
}

/**
 * Provides a tracker instance to the SolidJS CSS subtree.
 *
 * @param props Provider configuration and descendant content.
 * @returns A Solid context provider wrapping the supplied children.
 */
export const AnalyticsProvider: ParentComponent<AnalyticsProviderProps> = (
  props,
) => {
  const tracker = createTracker(
    props.storage
      ? ({ ...props.config, storage: props.storage } as TrackerConfigInput)
      : props.config,
  );

  const [consentSettings, setConsentSettings] =
    createSignal<ConsentSettings | null>(props.initialConsent ?? null);

  const updateConsent = (settings: ConsentSettings): void => {
    setConsentSettings(settings);
    void tracker.consent(settings);
  };

  if (props.initialConsent) {
    void tracker.consent(props.initialConsent);
  }

  return (
    <AnalyticsCtx.Provider value={tracker}>
      <AnalyticsConsentContext.Provider
        value={{ consentSettings, updateConsent }}
      >
        <AnalyticsCopyContext.Provider value={props.translator ?? null}>
          {props.children}
        </AnalyticsCopyContext.Provider>
      </AnalyticsConsentContext.Provider>
    </AnalyticsCtx.Provider>
  );
};
